<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  fetchMediaComplianceRules,
  fetchMediaComplianceRuleById,
  createMediaComplianceRule,
  updateMediaComplianceRule,
  deleteMediaComplianceRule,
  vectorizeMediaComplianceRuleImage,
  fetchMediaComplianceRuleReferenceImage,
  previewMediaComplianceRuleMatches,
  applyMediaComplianceRuleToExisting,
  getMediaUrl,
} from "@/services/backofficeClient";
import { summarizeActions } from "@/utils/complianceActions";
import ComplianceActionsEditor from "@/components/ComplianceActionsEditor.vue";
import { ROLES } from "@/services/acl";

const route = useRoute();
const router = useRouter();
const store = useStore();
const canCreateRule = computed(() =>
  store.getters.hasRole(ROLES.MEDIA_COMPLIANCE_RULE_CREATE)
);
const canEditRule = computed(() =>
  store.getters.hasRole(ROLES.MEDIA_COMPLIANCE_RULE_EDIT)
);
const canDeleteRule = computed(() =>
  store.getters.hasRole(ROLES.MEDIA_COMPLIANCE_RULE_DELETE)
);

const rules = ref([]);
const rulesLoading = ref(true);
const rulesError = ref("");

const selectedRuleId = ref(null);
const saving = ref(false);
const deleting = ref(false);
const formMessage = ref("");
const formMessageType = ref("success");
const actionsEditorRef = ref(null);

const DEFAULT_CNN_THRESHOLD = 0.95;
const DEFAULT_FACE_THRESHOLD = 0.8;
const defaultThresholdForType = (type) =>
  type === "face" ? DEFAULT_FACE_THRESHOLD : DEFAULT_CNN_THRESHOLD;

const mediaList = ref([]);
const vectorizing = ref(false);
const vectorFileInput = ref(null);

const PREVIEW_PAGE_SIZE = 100;

const previewLoading = ref(false);
const previewError = ref("");
const previewResults = ref(null);
const previewPage = ref(1);
const previewPagination = ref({
  page: 1,
  pageSize: PREVIEW_PAGE_SIZE,
  totalItems: 0,
  totalPages: 0,
  hasNext: false,
});

const resetPreview = () => {
  previewResults.value = null;
  previewError.value = "";
  previewPage.value = 1;
  previewPagination.value = {
    page: 1,
    pageSize: PREVIEW_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
  };
};

const form = reactive({
  name: "",
  isActive: true,
  createResolvedComplaint: false,
});

const isEditing = computed(() => Boolean(selectedRuleId.value));
const isEmptyList = computed(
  () => !rulesLoading.value && !rulesError.value && rules.value.length === 0
);

const normalizeRule = (rule) => ({
  id: rule?.id || rule?._id || "",
  name: rule?.name || "",
  mediaCount: rule?.mediaCount || 0,
  vectorCount: rule?.vectorCount || 0,
  actions: Array.isArray(rule?.actions) ? rule.actions : [],
  isActive: rule?.isActive !== false,
  createResolvedComplaint: rule?.createResolvedComplaint || false,
});

const revokeVectorPreviews = () => {
  for (const media of mediaList.value) {
    if (media.previewUrl) URL.revokeObjectURL(media.previewUrl);
  }
};

// Reference images live in private storage, so unlike getMediaUrl() this
// can't be a plain <img src>: it needs an authenticated fetch per image.
// Fire-and-forget; `media` is a reactive object, so setting previewUrl once
// the fetch resolves re-renders the thumbnail in place.
const loadMediaPreview = async (media) => {
  if (media.previewUrl || !media.imagePath) return;
  try {
    const blob = await fetchMediaComplianceRuleReferenceImage(media.imagePath);
    media.previewUrl = URL.createObjectURL(blob);
  } catch (error) {
    console.error("Failed to load reference image preview", error);
  }
};

const applyRuleToForm = (rule) => {
  selectedRuleId.value = rule?.id || rule?._id || null;
  form.name = rule?.name || "";
  form.isActive = rule?.isActive !== false;
  form.createResolvedComplaint = rule?.createResolvedComplaint || false;
  revokeVectorPreviews();
  mediaList.value = (Array.isArray(rule?.media) ? rule.media : []).map((m) => ({
    imagePath: m.imagePath || "",
    label: m.label || "",
    previewUrl: null,
    vectors: (Array.isArray(m.vectors) ? m.vectors : []).map((v) => ({
      type: v.type || "cnn",
      vector: v.vector || [],
      threshold: v.threshold ?? defaultThresholdForType(v.type),
    })),
  }));
  for (const media of mediaList.value) {
    loadMediaPreview(media);
  }
  actionsEditorRef.value?.reset(rule?.actions);
  formMessage.value = "";
  formMessageType.value = "success";
  resetPreview();
};

const resetForm = () => {
  selectedRuleId.value = null;
  form.name = "";
  form.isActive = true;
  form.createResolvedComplaint = false;
  revokeVectorPreviews();
  mediaList.value = [];
  actionsEditorRef.value?.reset([]);
  formMessage.value = "";
  formMessageType.value = "success";
  resetPreview();
};

const loadRules = async () => {
  rulesLoading.value = true;
  rulesError.value = "";
  try {
    const payload = await fetchMediaComplianceRules();
    const list = Array.isArray(payload) ? payload : [];
    rules.value = list.map(normalizeRule);
  } catch (error) {
    console.error("Failed to fetch media compliance rules", error);
    rulesError.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load media compliance rules.";
  } finally {
    rulesLoading.value = false;
  }
};

const selectRule = async (rule) => {
  if (!rule?.id) return;
  selectedRuleId.value = rule.id;
  formMessage.value = "";
  saving.value = false;
  if (route.query.ruleId !== rule.id) {
    router.replace({ query: { ...route.query, ruleId: rule.id } });
  }
  try {
    const fullRule = await fetchMediaComplianceRuleById(rule.id);
    applyRuleToForm(fullRule);
  } catch (error) {
    console.error("Failed to fetch media compliance rule", error);
    formMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load rule details.";
    formMessageType.value = "danger";
  }
};

const onAddVectorFile = async (event) => {
  const files = Array.from(event.target.files || []);
  event.target.value = "";
  if (!files.length) return;
  vectorizing.value = true;
  formMessage.value = "";
  const failedFiles = [];
  try {
    for (const file of files) {
      try {
        const result = await vectorizeMediaComplianceRuleImage(file);
        const resultVectors = Array.isArray(result?.vectors) ? result.vectors : [];
        const faceCount = resultVectors.filter((v) => v.type === "face").length;
        mediaList.value.push({
          imagePath: result?.imagePath || "",
          label: file.name,
          previewUrl: URL.createObjectURL(file),
          vectors: resultVectors
            .filter((v) => v.type !== "face" || faceCount <= 1)
            .map((v) => ({
              type: v.type,
              vector: v.vector || [],
              threshold: defaultThresholdForType(v.type),
            })),
        });
      } catch (error) {
        console.error("Failed to vectorize reference image", error);
        failedFiles.push(
          `${file.name}: ${error?.payload?.message || error?.message || "Failed to vectorize image."}`
        );
      }
    }
    resetPreview();
    if (failedFiles.length) {
      formMessage.value = failedFiles.join("; ");
      formMessageType.value = "danger";
    }
  } finally {
    vectorizing.value = false;
  }
};

const removeMedia = (index) => {
  const [removed] = mediaList.value.splice(index, 1);
  if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
  resetPreview();
};

const removeVectorEntry = (mediaIndex, vectorIndex) => {
  mediaList.value[mediaIndex]?.vectors.splice(vectorIndex, 1);
  resetPreview();
};

const runPreview = async (page = 1) => {
  const previewVectors = mediaList.value.flatMap((m) =>
    m.vectors.map((v) => ({
      type: v.type,
      vector: v.vector,
      threshold: Number(v.threshold),
    }))
  );
  if (!previewVectors.length) {
    previewError.value = "Add at least one reference image first.";
    return;
  }
  previewLoading.value = true;
  previewError.value = "";
  try {
    const payload = await previewMediaComplianceRuleMatches(previewVectors, {
      page,
      pageSize: PREVIEW_PAGE_SIZE,
    });
    previewResults.value = Array.isArray(payload?.items) ? payload.items : [];
    previewPagination.value = payload?.pagination || {
      page,
      pageSize: PREVIEW_PAGE_SIZE,
      totalItems: previewResults.value.length,
      totalPages: previewResults.value.length ? 1 : 0,
      hasNext: false,
    };
    previewPage.value = previewPagination.value.page || page;
  } catch (error) {
    console.error("Failed to preview media compliance rule matches", error);
    previewError.value =
      error?.payload?.message || error?.message || "Failed to preview matches.";
  } finally {
    previewLoading.value = false;
  }
};

const changePreviewPage = (delta) => {
  const nextPage = previewPage.value + delta;
  if (nextPage < 1 || nextPage > previewPagination.value.totalPages) return;
  runPreview(nextPage);
};

const submitForm = async () => {
  formMessage.value = "";
  formMessageType.value = "success";
  saving.value = true;
  try {
    const name = (form.name || "").trim();
    if (!name) {
      throw new Error("Rule name is required.");
    }
    if (!mediaList.value.length) {
      throw new Error("Add at least one reference image.");
    }
    for (const media of mediaList.value) {
      if (!media.vectors.length) {
        throw new Error(`${media.label || media.imagePath} has no vectors left.`);
      }
      for (const vector of media.vectors) {
        const threshold = Number(vector.threshold);
        if (!Number.isFinite(threshold) || threshold <= 0 || threshold > 1) {
          throw new Error(
            `Each vector threshold must be a number greater than 0 and up to 1.`
          );
        }
      }
    }
    const payload = {
      name,
      isActive: !!form.isActive,
      createResolvedComplaint: !!form.createResolvedComplaint,
      media: mediaList.value.map((m) => ({
        imagePath: m.imagePath,
        label: m.label,
        vectors: m.vectors.map((v) => ({
          type: v.type,
          vector: v.vector,
          threshold: Number(v.threshold),
        })),
      })),
      actions: actionsEditorRef.value.getPayload(),
    };

    let response;
    if (isEditing.value) {
      response = await updateMediaComplianceRule(selectedRuleId.value, payload);
      formMessage.value = "Rule updated.";
    } else {
      response = await createMediaComplianceRule(payload);
      formMessage.value = "Rule created.";
    }
    formMessageType.value = "success";

    const newId = response?.id || response?._id || selectedRuleId.value;
    await loadRules();
    if (newId) {
      const fresh = await fetchMediaComplianceRuleById(newId).catch(() => response);
      applyRuleToForm(fresh || payload);
    } else if (!isEditing.value) {
      resetForm();
    }
  } catch (error) {
    console.error("Failed to save media compliance rule", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to save rule.";
    formMessageType.value = "danger";
  } finally {
    saving.value = false;
  }
};

const applyingToExisting = ref(false);

const applyToExistingUsers = async () => {
  if (!selectedRuleId.value) {
    formMessage.value = "Save the rule before applying it to existing users.";
    formMessageType.value = "danger";
    return;
  }
  const confirmApply = window.confirm(
    "This will flag every already-uploaded media that matches the saved " +
      "rule as a violation for the reporting/action steps set on it, " +
      "same as a newly uploaded matching photo would be. Continue?"
  );
  if (!confirmApply) return;

  formMessage.value = "";
  formMessageType.value = "success";
  applyingToExisting.value = true;
  try {
    const result = await applyMediaComplianceRuleToExisting(selectedRuleId.value);
    const matched = result?.matched ?? 0;
    const flagged = result?.flagged ?? 0;
    formMessage.value = `${matched} existing media matched; ${flagged} newly flagged (the rest were already flagged).`;
    formMessageType.value = "success";
  } catch (error) {
    console.error("Failed to apply media compliance rule to existing users", error);
    formMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to apply rule to existing users.";
    formMessageType.value = "danger";
  } finally {
    applyingToExisting.value = false;
  }
};

const deleteRule = async () => {
  if (!selectedRuleId.value) {
    formMessage.value = "Select a rule to delete.";
    formMessageType.value = "danger";
    return;
  }
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this media compliance rule?"
  );
  if (!confirmDelete) return;

  formMessage.value = "";
  formMessageType.value = "success";
  deleting.value = true;
  try {
    await deleteMediaComplianceRule(selectedRuleId.value);
    formMessage.value = "Rule deleted.";
    formMessageType.value = "success";
    resetForm();
    await loadRules();
  } catch (error) {
    console.error("Failed to delete media compliance rule", error);
    formMessage.value =
      error?.payload?.message || error?.message || "Failed to delete rule.";
    formMessageType.value = "danger";
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  resetForm();
  await loadRules();
  const requestedRuleId = route.query.ruleId;
  if (requestedRuleId) {
    await selectRule({ id: requestedRuleId });
  }
});

onBeforeUnmount(() => {
  revokeVectorPreviews();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12 col-lg-5">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 class="mb-1">Media Compliance Rules</h6>
              <p class="text-xs text-secondary mb-0">
                Select a rule to edit or start a new one.
              </p>
            </div>
            <div class="d-flex gap-2">
              <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="rulesLoading"
                @click="resetForm">
                New rule
              </button>
              <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="rulesLoading"
                @click="loadRules">
                <i class="fas fa-sync me-2"></i>
                {{ rulesLoading ? "Refreshing..." : "Refresh" }}
              </button>
            </div>
          </div>

          <div v-if="rulesLoading" class="card-body text-center py-5">
            <div class="spinner-border text-success" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 mb-0 text-sm text-secondary">
              Loading media compliance rules...
            </p>
          </div>

          <div v-else-if="rulesError" class="card-body text-center py-5">
            <div class="icon icon-shape bg-gradient-danger text-white shadow text-center border-radius-md mx-auto">
              <i class="ni ni-fat-remove"></i>
            </div>
            <p class="mt-3 text-sm">{{ rulesError }}</p>
            <button class="btn btn-sm btn-success" type="button" @click="loadRules">
              Try again
            </button>
          </div>

          <div v-else class="card-body">
            <div v-if="isEmptyList" class="text-center py-5">
              <p class="text-sm text-secondary mb-2">No media compliance rules yet.</p>
              <button class="btn btn-sm btn-success" type="button" @click="resetForm">
                Create first rule
              </button>
            </div>
            <div v-else class="table-responsive">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Name
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Media
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Vectors
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                      Actions
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                      Active
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rule in rules" :key="rule.id || rule.name"
                    :class="rule.id === selectedRuleId ? 'bg-light' : ''" style="cursor: pointer"
                    @click="selectRule(rule)">
                    <td>
                      <div class="d-flex flex-column">
                        <span class="text-sm font-weight-bold">
                          {{ rule.name || "Untitled" }}
                        </span>
                        <span class="text-xs text-secondary">
                          {{ rule.id }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ rule.mediaCount }}
                      </span>
                    </td>
                    <td>
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ rule.vectorCount }}
                      </span>
                    </td>
                    <td>
                      <span class="badge bg-light text-dark border text-xxs">
                        {{ summarizeActions(rule.actions) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <span class="badge text-xxs"
                        :class="rule.isActive ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                        {{ rule.isActive ? "Active" : "Disabled" }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-7 mt-4 mt-lg-0">
        <div class="card shadow-sm">
          <div class="card-header pb-0 border-0 d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h6 class="mb-1">
                {{ isEditing ? "Edit media compliance rule" : "Create media compliance rule" }}
              </h6>
              <p class="text-xs text-secondary mb-0">
                Upload reference images to derive their CNN/face vectors, set a
                similarity threshold per vector, and preview which existing media
                already match.
              </p>
            </div>
            <div class="text-end" v-if="isEditing && selectedRuleId">
              <span class="badge bg-light text-dark border text-xxs">
                ID: {{ selectedRuleId }}
              </span>
            </div>
          </div>

          <div class="card-body">
            <div v-if="formMessage" class="alert py-2 px-3"
              :class="formMessageType === 'danger' ? 'alert-danger' : 'alert-success'">
              <span class="text-sm">{{ formMessage }}</span>
            </div>

            <div class="mb-3">
              <label class="form-label text-sm fw-semibold">Rule name</label>
              <input v-model="form.name" type="text" class="form-control" placeholder="e.g. Known banned image set"
                :disabled="saving" />
            </div>

            <div class="form-check form-switch mb-4">
              <input class="form-check-input" type="checkbox" id="mediaRuleIsActive" v-model="form.isActive"
                :disabled="saving" />
              <label class="form-check-label text-sm" for="mediaRuleIsActive">Active</label>
            </div>

            <div class="form-check form-switch mb-4">
              <input class="form-check-input" type="checkbox" id="mediaRuleCreateResolvedComplaint"
                v-model="form.createResolvedComplaint" :disabled="saving" />
              <label class="form-check-label text-sm" for="mediaRuleCreateResolvedComplaint">
                Create Resolved Complaint
              </label>
            </div>

            <hr />

            <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
              <label class="form-label text-sm fw-semibold mb-0">Reference media</label>
              <div>
                <input ref="vectorFileInput" type="file" accept="image/*" multiple class="d-none" @change="onAddVectorFile" />
                <button v-if="canEditRule" class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="saving || vectorizing"
                  @click="vectorFileInput.click()">
                  {{ vectorizing ? "Vectorizing..." : "Add reference image" }}
                </button>
              </div>
            </div>

            <div v-if="!mediaList.length" class="text-xs text-secondary mb-3">
              No reference media yet. Add at least one image.
            </div>
            <div v-else class="mb-3">
              <div v-for="(media, mIndex) in mediaList" :key="mIndex"
                class="border rounded p-2 mb-2 d-flex gap-3">
                <div style="width: 90px; flex-shrink: 0">
                  <img v-if="media.previewUrl" :src="media.previewUrl" class="w-100 rounded"
                    style="height: 90px; object-fit: cover" />
                  <div v-else
                    class="w-100 rounded bg-light d-flex align-items-center justify-content-center text-secondary"
                    style="height: 90px">
                    <i class="fas fa-image"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <input v-model="media.label" type="text" class="form-control form-control-sm mb-2"
                    placeholder="Label" :disabled="saving" />
                  <div v-if="!media.vectors.length" class="text-xs text-secondary">
                    No vectors left on this image.
                  </div>
                  <div v-for="(vector, vIndex) in media.vectors" :key="vIndex"
                    class="d-flex align-items-center gap-2 mb-1">
                    <span class="badge bg-light text-dark border text-xxs" style="width: 50px">
                      {{ vector.type }}
                    </span>
                    <input v-model.number="vector.threshold" type="number" min="0.01" max="1" step="0.01"
                      class="form-control form-control-sm" style="width: 90px" placeholder="threshold"
                      :disabled="saving" />
                    <button class="btn btn-sm btn-outline-danger mb-0" type="button" :disabled="saving"
                      @click="removeVectorEntry(mIndex, vIndex)">
                      &times;
                    </button>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-danger mb-0 align-self-start" type="button" :disabled="saving"
                  @click="removeMedia(mIndex)">
                  Remove
                </button>
              </div>
            </div>

            <hr />

            <ComplianceActionsEditor ref="actionsEditorRef" :disabled="saving" />

            <div class="d-flex align-items-center gap-2 mt-4">
              <button v-if="isEditing ? canEditRule : canCreateRule" class="btn btn-success mb-0" type="button" :disabled="saving || deleting" @click="submitForm">
                {{ saving ? "Saving..." : isEditing ? "Update rule" : "Create rule" }}
              </button>
              <button class="btn btn-outline-secondary mb-0" type="button" :disabled="saving || deleting"
                @click="resetForm">
                Clear
              </button>
              <button v-if="isEditing && canDeleteRule" class="btn btn-outline-danger mb-0 ms-auto" type="button"
                :disabled="saving || deleting" @click="deleteRule">
                {{ deleting ? "Deleting..." : "Delete rule" }}
              </button>
            </div>

            <hr />

            <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
              <label class="form-label text-sm fw-semibold mb-0">
                Preview matches for reference vectors
              </label>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-success mb-0" type="button"
                  :disabled="previewLoading || !mediaList.length" @click="runPreview(1)">
                  {{ previewLoading ? "Previewing..." : "Preview matches" }}
                </button>
                <button v-if="isEditing && canEditRule" class="btn btn-sm btn-outline-warning mb-0" type="button"
                  :disabled="applyingToExisting || saving" @click="applyToExistingUsers">
                  {{ applyingToExisting ? "Applying..." : "Apply to existing users" }}
                </button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-2">
              Checks the current threshold against already-vectorized media in the system,
              using the reference images added above, so you can sanity-check it before saving.
              "Apply to existing users" runs the same check against the saved rule and, for every
              match, creates a complaint and applies the rule's actions — save your changes first
              so it uses the latest thresholds/actions.
            </p>

            <div v-if="previewError" class="alert alert-danger py-2 px-3">
              <span class="text-sm">{{ previewError }}</span>
            </div>

            <div v-if="previewResults && !previewResults.length" class="text-xs text-secondary">
              No matching media found at this threshold.
            </div>
            <template v-else-if="previewResults && previewResults.length">
              <div class="d-flex flex-wrap gap-3">
                <div v-for="item in previewResults" :key="item.path" class="border rounded p-2 text-center"
                  style="width: 120px">
                  <img :src="getMediaUrl(item.path)" class="w-100 rounded mb-1"
                    style="height: 90px; object-fit: cover" />
                  <div class="text-xs font-weight-bold">
                    {{ (item.similarityScore * 100).toFixed(1) }}%
                  </div>
                  <router-link :to="`/users/${item.userId}`" class="text-xs text-truncate d-block"
                    :title="item.user?.name">
                    {{ item.user?.name || item.userId }}
                  </router-link>
                  <span class="badge text-xxs mt-1"
                    :class="item.user?.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                    {{ item.user?.status || "unknown" }}
                  </span>
                </div>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 pt-3 mt-2 border-top">
                <p class="mb-0 text-xs text-secondary">
                  {{ previewPagination.totalItems }} match{{ previewPagination.totalItems === 1 ? "" : "es" }}
                </p>
                <div class="ms-auto d-flex align-items-center gap-2">
                  <span class="text-xs text-secondary">
                    Page {{ previewPagination.page }} of {{ Math.max(previewPagination.totalPages, 1) }}
                  </span>
                  <div class="btn-group btn-group-sm">
                    <button type="button" class="btn btn-outline-secondary"
                      :disabled="previewLoading || previewPage <= 1" @click="changePreviewPage(-1)">
                      Previous
                    </button>
                    <button type="button" class="btn btn-outline-secondary"
                      :disabled="previewLoading || previewPage >= previewPagination.totalPages"
                      @click="changePreviewPage(1)">
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
